import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { LogOut, Package, CheckCircle, Truck, Clock, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_MAP = {
  pending: { label: "Oczekuje", color: "text-yellow-400", icon: Clock },
  paid: { label: "Opłacone", color: "text-green-400", icon: CheckCircle },
  shipped: { label: "Wysłane", color: "text-blue-400", icon: Truck },
};

function AdminLogin({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/admin/login`, { username, password }, { withCredentials: true });
      onLogin(res.data.token);
    } catch (err) {
      setError(err.response?.data?.detail || "Błąd logowania");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-4" data-testid="admin-login-page">
      <div className="w-full max-w-sm">
        <h1 className="font-['Oswald'] text-3xl uppercase tracking-tighter text-white mb-2 text-center">
          Panel Admina
        </h1>
        <p className="text-[#A0B0C4] text-sm text-center mb-8 font-['Manrope']">Pueblo — Preludium</p>

        <form onSubmit={handleSubmit} className="space-y-6 bg-[#192D48]/20 border border-[#32558B]/20 p-8">
          <div>
            <label className="font-['Manrope'] text-xs uppercase tracking-widest text-[#A0B0C4] mb-2 block">Login</label>
            <input
              data-testid="admin-login-username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="form-input-dark"
              required
            />
          </div>
          <div>
            <label className="font-['Manrope'] text-xs uppercase tracking-widest text-[#A0B0C4] mb-2 block">Hasło</label>
            <input
              data-testid="admin-login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="form-input-dark"
              required
            />
          </div>
          {error && <p data-testid="admin-login-error" className="text-[#EB0023] text-sm font-['Manrope']">{error}</p>}
          <button
            data-testid="admin-login-submit"
            type="submit"
            disabled={loading}
            className="w-full bg-[#EB0023] text-white font-['Oswald'] text-lg uppercase tracking-wider py-3 hover:bg-white hover:text-black transition-all duration-300 disabled:opacity-50"
          >
            {loading ? "Logowanie..." : "Zaloguj"}
          </button>
        </form>
      </div>
    </div>
  );
}

function OrdersTable({ token, onLogout }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/orders`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });
      setOrders(res.data);
    } catch (err) {
      if (err.response?.status === 401) onLogout();
    } finally {
      setLoading(false);
    }
  }, [token, onLogout]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const updateStatus = async (orderId, newStatus) => {
    try {
      await axios.patch(`${API}/admin/orders/${orderId}`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });
      setOrders((prev) => prev.map((o) => o.id === orderId ? { ...o, status: newStatus } : o));
    } catch (err) {
      console.error("Status update failed", err);
    }
  };

  const stats = {
    total: orders.length,
    pending: orders.filter((o) => o.status === "pending").length,
    paid: orders.filter((o) => o.status === "paid").length,
    shipped: orders.filter((o) => o.status === "shipped").length,
  };

  return (
    <div className="min-h-screen bg-black" data-testid="admin-dashboard">
      {/* Header */}
      <div className="border-b border-[#32558B]/20 bg-[#192D48]/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Package className="w-5 h-5 text-[#EB0023]" />
            <h1 className="font-['Oswald'] text-xl uppercase tracking-tighter text-white">Zamówienia Preorder</h1>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={fetchOrders} className="text-[#A0B0C4] hover:text-white transition-colors" data-testid="admin-refresh">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button
              onClick={onLogout}
              data-testid="admin-logout"
              className="flex items-center gap-2 text-[#A0B0C4] hover:text-[#EB0023] transition-colors font-['Manrope'] text-sm"
            >
              <LogOut className="w-4 h-4" /> Wyloguj
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: "Wszystkie", value: stats.total, color: "text-white" },
            { label: "Oczekujące", value: stats.pending, color: "text-yellow-400" },
            { label: "Opłacone", value: stats.paid, color: "text-green-400" },
            { label: "Wysłane", value: stats.shipped, color: "text-blue-400" },
          ].map((s) => (
            <div key={s.label} className="bg-[#192D48]/20 border border-[#32558B]/20 p-4" data-testid={`stat-${s.label.toLowerCase()}`}>
              <p className="font-['Manrope'] text-xs uppercase tracking-widest text-[#A0B0C4] mb-1">{s.label}</p>
              <p className={`font-['Oswald'] text-3xl ${s.color}`}>{s.value}</p>
            </div>
          ))}
        </div>

        {/* Orders */}
        {loading ? (
          <p className="text-[#A0B0C4] text-center py-12 font-['Manrope']">Ładowanie...</p>
        ) : orders.length === 0 ? (
          <p className="text-[#A0B0C4] text-center py-12 font-['Manrope']">Brak zamówień</p>
        ) : (
          <div className="space-y-3">
            {orders.map((order) => {
              const st = STATUS_MAP[order.status] || STATUS_MAP.pending;
              const StIcon = st.icon;
              return (
                <div
                  key={order.id}
                  data-testid={`order-card-${order.id}`}
                  className="bg-[#192D48]/20 border border-[#32558B]/20 p-5 hover:border-[#32558B]/40 transition-colors"
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div>
                        <p className="font-['Manrope'] text-xs uppercase tracking-widest text-[#A0B0C4] mb-1">Klient</p>
                        <p className="text-white font-['Manrope'] font-semibold">{order.name}</p>
                        <p className="text-[#A0B0C4] text-sm">{order.email}</p>
                        <p className="text-[#A0B0C4] text-sm">{order.phone}</p>
                      </div>
                      <div>
                        <p className="font-['Manrope'] text-xs uppercase tracking-widest text-[#A0B0C4] mb-1">Adres wysyłki</p>
                        <p className="text-white font-['Manrope']">{order.address}</p>
                        <p className="text-white font-['Manrope']">{order.postal_code} {order.city}</p>
                      </div>
                      <div>
                        <p className="font-['Manrope'] text-xs uppercase tracking-widest text-[#A0B0C4] mb-1">Data</p>
                        <p className="text-white font-['Manrope'] text-sm">
                          {order.created_at ? new Date(order.created_at).toLocaleString("pl-PL") : "—"}
                        </p>
                      </div>
                      <div>
                        <p className="font-['Manrope'] text-xs uppercase tracking-widest text-[#A0B0C4] mb-1">Status</p>
                        <div className={`flex items-center gap-2 ${st.color}`}>
                          <StIcon className="w-4 h-4" />
                          <span className="font-['Manrope'] font-semibold text-sm">{st.label}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      {order.status !== "paid" && (
                        <button
                          onClick={() => updateStatus(order.id, "paid")}
                          data-testid={`mark-paid-${order.id}`}
                          className="px-3 py-1.5 bg-green-500/20 text-green-400 border border-green-500/30 text-xs font-['Manrope'] uppercase tracking-wider hover:bg-green-500/30 transition-colors"
                        >
                          Opłacone
                        </button>
                      )}
                      {order.status !== "shipped" && (
                        <button
                          onClick={() => updateStatus(order.id, "shipped")}
                          data-testid={`mark-shipped-${order.id}`}
                          className="px-3 py-1.5 bg-blue-500/20 text-blue-400 border border-blue-500/30 text-xs font-['Manrope'] uppercase tracking-wider hover:bg-blue-500/30 transition-colors"
                        >
                          Wysłane
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminPanel() {
  const [token, setToken] = useState(() => localStorage.getItem("admin_token"));

  const handleLogin = (newToken) => {
    localStorage.setItem("admin_token", newToken);
    setToken(newToken);
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    setToken(null);
  };

  if (!token) return <AdminLogin onLogin={handleLogin} />;
  return <OrdersTable token={token} onLogout={handleLogout} />;
}
